import { parseUsdPerGaju } from '@xchange/core';
import type {
  MeResponse,
  OfferView,
  RewardsResponse,
  SignedBody,
  UnsignedView,
  WalletActivityItem,
  WalletResponse,
} from '@xchange/shared';
import { useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { api, type ActivityItem, type PairView, type PublicMarket } from '../api.js';
import { xgeText } from '../assets.js';
import { pollWhileVisible } from '../poll.js';
import { track, trackError } from '../track.js';
import {
  activeWalletId,
  listWallets,
  needsConfirmation,
  noteWalletSeen,
  startAnotherWallet,
  switchWallet,
  walletSeen,
} from '../wallet.js';
import { walletWorth } from '../worth.js';
import { ForgetWallet } from './ForgetWallet.js';
import { celebrate } from '../celebrate.js';
import { amountText, assetName, bareText, inputText, parseAmount, type Asset } from '../assets.js';
import { normalizeDecimal } from '../numbers.js';
import type { Platform } from '../platform/index.js';
import { netOfFee, priceBoxText } from '../prices.js';
import { bidReward, bidRewardText } from '../bidReward.js';
import { simpleBook } from '../simpleCap.js';
import { clearDraft, estimate, readDraft, simpleOrder } from '../ticket.js';
import { inviteText, referralLine, shareTargets } from '../referral.js';
import { signUnsigned } from '../sign.js';
import { toast } from '../toast.js';
import { markBusy } from '../update.js';
import { withWallet } from '../wallet.js';
import { aboutLocal, CURRENCIES, currencyName, localAmount, localOf } from '../localMoney.js';
import { useLocalMoney } from '../money.js';
import { asksOf, bidsOf, finished, spreadOf, tradesOf } from '../book.js';
import { PHONE_LEVELS, PHONE_TRADES, useNarrow } from '../narrow.js';
import { ago, GAJU_FEE_RESERVE, Mark, perCoin, perCoinUsd, quoteFor } from './Market.js';
import { SimpleTicket } from './SimpleTicket.js';
import { Earn, type BidPreset } from './Earn.js';
import { BuyUsdc, BuyUsdcButton } from './BuyUsdc.js';
import { Loading } from './Loading.js';
import { SharedLine } from '../sharedLine.js';
import { PinPad } from './PinPad.js';

/**
 * Trading in a browser, on a screen that can hold the whole market at once:
 * the pair and its figures, the book and the latest trades, the ticket, and
 * your orders and history below. Every action is the same call and the same
 * signature as in Telegram; the key never leaves the device, the PIN opens it
 * for one signature at a time.
 */
type Side = 'buy' | 'sell';
interface Pin {
  unsigned: UnsignedView;
  then: (signed: SignedBody, pin: string) => Promise<void>;
  onCancel?: () => void;
}
type Modal = 'none' | 'wallet' | 'token' | 'receive' | 'send' | 'account' | 'phrase' | 'forget';

export function WebTrade({
  platform,
  me,
  onMe,
}: {
  platform: Platform;
  me: MeResponse;
  onMe: (me: MeResponse) => void;
}) {
  // A ticket filled in on the welcome, before this wallet existed: it opens
  // here as it was left, in Simple, until it is placed or changed.
  const [draft] = useState(() => readDraft());
  const [pairs, setPairs] = useState<PairView[]>([]);
  const [pairName, setPairName] = useState(draft?.pair ?? 'XGE/USDC');
  const [open, setOpen] = useState<OfferView[]>([]);
  const [mine, setMine] = useState<OfferView[]>([]);
  const [activity, setActivity] = useState<ActivityItem[]>([]);
  const [wallet, setWallet] = useState<WalletResponse | null>(null);
  // Figures in the person's own money: the choice, the day's rates and the two
  // ways a figure is shown, shared with the screen inside Telegram.
  const money = useLocalMoney();
  const { currency, rates: usdPer, read, readAmount, readIn } = money;
  // A phone shows less at once: a short market strip, the next step only, four
  // levels a side, three trades, fewer rows before a scroll. A laptop has room
  // for all of it, so nothing there changes.
  const narrow = useNarrow();
  const [stepsOpen, setStepsOpen] = useState(false);
  const [market, setMarket] = useState<PublicMarket | null>(null);
  const [rewards, setRewards] = useState<RewardsResponse | null>(null);
  const [history, setHistory] = useState<(WalletActivityItem & { asset: Asset })[]>([]);
  const [below, setBelow] = useState<'orders' | 'history'>('orders');
  // Finished orders, all time: asked for when History opens, on Refresh and
  // when a trade of yours settles. The polled book answer keeps two days.
  const [ended, setEnded] = useState<OfferView[] | null>(null);
  const loadEnded = useCallback(async () => {
    setEnded((await api.history()).mine);
  }, []);
  const historyOpen = useRef(false);
  const [side, setSideState] = useState<Side>(draft?.side ?? 'buy');
  const [amount, setAmountState] = useState(draft?.amount ?? '');
  // Changing the ticket, or placing it, lets the welcome's draft go.
  const setSide = (s: Side): void => {
    if (s !== side) clearDraft();
    setSideState(s);
  };
  const setAmount = (text: string): void => {
    clearDraft();
    setAmountState(text);
  };
  const [price, setPrice] = useState('');
  const [pin, setPin] = useState<Pin | null>(null);
  const [busy, setBusy] = useState(false);
  const [modal, setModal] = useState<Modal>('none');
  const [placed, setPlaced] = useState(false);
  // Simple: one amount, filled at the best price on the book. Advanced: the
  // whole screen. Earn: the daily job. Newcomers start simple; Simple or
  // Advanced is remembered, Earn is not, so the app always opens on trading.
  const [mode, setModeState] = useState<'simple' | 'pro' | 'earn'>(() => {
    if (draft !== null) return 'simple';
    try {
      return localStorage.getItem('xchange.mode') === 'pro' ? 'pro' : 'simple';
    } catch {
      return 'simple';
    }
  });
  const setMode = (m: 'simple' | 'pro' | 'earn'): void => {
    setModeState(m);
    track('mode_switch', { mode: m });
    if (m === 'earn') return;
    try {
      localStorage.setItem('xchange.mode', m);
    } catch {
      /* fine */
    }
  };
  const [startDismissed, setStartDismissed] = useState<boolean>(() => {
    try {
      return localStorage.getItem('xchange.start.done') === '1';
    } catch {
      return false;
    }
  });
  /** What was true at the last look, so a change can be celebrated once. */
  const seen = useRef<{ trades: number; funded: boolean; rewards: number } | null>(null);
  const [modalAsset, setModalAsset] = useState<Asset>('GAJU');
  const [sendTo, setSendTo] = useState('');
  const [sendAmount, setSendAmount] = useState('');
  const [nameDraft, setNameDraft] = useState(me.displayName);
  const [phrase, setPhrase] = useState<string | null>(null);
  // The wallet whose removal card is open, and whose words "Show the words" opens.
  const [forgetId, setForgetId] = useState<string | null>(null);
  // Bumped when a wallet leaves the list, so the list is read again.
  const [, setWalletsTick] = useState(0);
  // The first figures have arrived; until then the screen waits as one.
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    markBusy(busy || pin !== null);
    return () => {
      markBusy(false);
    };
  }, [busy, pin]);

  const load = useCallback(async () => {
    const [p, w, r, a, m, rw] = await Promise.all([
      api.pairs().catch(() => [] as PairView[]),
      api.wallet().catch(() => null),
      api.offers(),
      api.activity().catch(() => [] as ActivityItem[]),
      api.publicMarket().catch(() => null),
      api.rewards().catch(() => null),
    ]);
    setPairs(p);
    if (w !== null) setWallet(w);
    setOpen(r.open);
    setMine(r.mine);
    setActivity(a);
    if (m !== null) setMarket(m);
    if (rw !== null) setRewards(rw);
    // Something new since the last look: say so, once, with feeling.
    const tradesNow = r.mine.reduce(
      (n, o) => n + o.fills.filter((f) => f.status === 'done').length,
      0,
    );
    const fundedNow =
      w !== null && Object.values(w.balances).some((b) => b !== null && BigInt(b) > 0n);
    const rewardsNow = rw === null ? 0 : Number(rw.inWallet);
    const was = seen.current;
    if (was !== null) {
      if (tradesNow > was.trades) {
        if (historyOpen.current) void loadEnded().catch(() => undefined);
        track('trade_done', { first: was.trades === 0 });
        celebrate();
        toast.ok(
          was.trades === 0
            ? 'Your first swap is done. It settled on chain, into your own wallet.'
            : 'Swap done. Settled on chain.',
        );
      }
      if (fundedNow && !was.funded) {
        track('funded');
        celebrate();
        toast.ok('Funds arrived. You can swap now.');
      }
      if (rewardsNow > was.rewards) {
        celebrate();
        toast.ok('XGE rewards arrived in your wallet.');
      }
    }
    seen.current = { trades: tradesNow, funded: fundedNow, rewards: rewardsNow };
    setLoaded(true);
  }, [loadEnded]);
  const loadHistory = useCallback(async () => {
    const assets: Asset[] = ['GAJU', 'USDC', 'XGE'];
    const lists = await Promise.all(
      assets.map((asset) =>
        api
          .walletActivity(asset)
          .then((items) => items.map((i) => ({ ...i, asset })))
          .catch(() => [] as (WalletActivityItem & { asset: Asset })[]),
      ),
    );
    setHistory(lists.flat().sort((x, y) => (x.at < y.at ? 1 : -1)));
  }, []);
  useEffect(() => {
    void load().catch(() => undefined);
    void loadHistory().catch(() => undefined);
    return pollWhileVisible(() => {
      void load().catch(() => undefined);
      void loadHistory().catch(() => undefined);
    }, 10_000);
  }, [load, loadHistory]);

  const pair = pairs.find((p) => p.name === pairName) ?? pairs[0];
  const base = (pair?.base ?? 'GAJU') as Asset;
  const quote = (pair?.quote ?? 'USDC') as Asset;
  const perOne = base === 'GAJU';

  const asks = useMemo(() => asksOf(open, pair?.name), [open, pair]);
  const bids = useMemo(() => bidsOf(open, pair?.name), [open, pair]);
  const trades = tradesOf(activity, pair?.name, narrow ? PHONE_TRADES : 14);
  // Your orders are the ones you placed. A part you took of someone else's
  // offer is a trade, and lives in History once it is done.
  const orders = mine.filter((o) => o.mine === 'maker' && !finished(o.status));

  // What the open wallet holds, kept on this device, so its removal card can
  // say so later while another wallet is open. Amounts and a count only.
  useEffect(() => {
    const id = activeWalletId();
    if (!loaded || wallet === null || id === null) return;
    const w = walletWorth(wallet.balances, pairs);
    noteWalletSeen(id, {
      gaju: wallet.balances.GAJU,
      usdc: wallet.balances.USDC,
      xge: wallet.balances.XGE,
      worth: w === null ? null : w.toString(),
      orders: orders.length,
      at: new Date().toISOString(),
    });
  }, [loaded, wallet, pairs, orders.length]);

  // History: finished orders from /api/history, with what came and went in the
  // wallet that is not an order (received, sent, rewards), newest first.
  const historyRows = useMemo(() => {
    const rows: {
      key: string;
      at: string;
      text: string;
      amount: string;
      /** The dollars behind the amount, for the chosen currency; null when it is not money. */
      usd: number | null;
      sign: '+' | '-' | '';
      url: string | null;
    }[] = [];
    for (const o of ended ?? []) {
      const p = pairs.find((x) => x.name === o.pair);
      const baseAsset = (p?.base ?? 'GAJU') as Asset;
      const role = o.mine === 'taker' ? 'taker' : 'maker';
      const done = o.fills.filter(
        (f) => f.status === 'done' && (role === 'maker' || f.mine === 'taker'),
      );
      const filled = done.reduce((n, f) => n + BigInt(f.baseAmount), 0n);
      const label =
        role === 'taker'
          ? o.side === 'sell'
            ? 'Bought'
            : 'Sold'
          : o.side === 'buy'
            ? 'Bid'
            : 'Offer';
      const at = [o.createdAt, ...o.fills.map((f) => f.createdAt)].sort().at(-1) ?? o.createdAt;
      const units = filled > 0n ? filled : BigInt(o.baseAmount);
      rows.push({
        key: `offer:${o.id}`,
        at,
        text: `${label} · ${p?.display ?? o.pair} · ${role === 'taker' ? 'Done' : o.statusText}`,
        amount: amountText(baseAsset, units),
        usd: baseAsset === 'USDC' ? Number(units) / 1e6 : null,
        sign: '',
        url: o.makerTxUrl,
      });
    }
    for (const h of history) {
      if (h.kind !== 'received' && h.kind !== 'sent' && h.kind !== 'reward') continue;
      const out = h.amount.startsWith('-');
      const units = BigInt(h.amount.replace('-', ''));
      rows.push({
        key: `${h.asset}:${h.id}`,
        at: h.at,
        text: h.text,
        amount: `${out ? '−' : '+'}${bareText(h.asset, units)}`,
        usd: h.asset === 'USDC' ? Number(units) / 1e6 : null,
        sign: out ? '-' : '+',
        url: h.url,
      });
    }
    return rows.sort((x, y) => (x.at < y.at ? 1 : -1)).slice(0, 200);
  }, [ended, history, pairs]);

  // The price box: USD per Gaju for Gaju, quote per coin otherwise. The
  // stored unit is quote smallest units per price unit of base.
  const priceUnits = (): bigint => {
    if (pair === undefined) return 0n;
    const text = normalizeDecimal(price);
    return perOne ? parseUsdPerGaju(text) : parseAmount(quote, text);
  };
  // Simple trades at the best price by someone else, and only what is there.
  const simple = useMemo(() => simpleBook(side === 'buy' ? asks : bids), [asks, bids, side]);
  const prefill = (s: Side): void => {
    if (pair === undefined) return;
    if (mode === 'simple') {
      const sb = simpleBook(s === 'buy' ? asks : bids);
      setPrice(sb === null ? '' : priceBoxText(pair, sb.price, perOne));
      return;
    }
    const best = s === 'buy' ? (asks[0] ?? bids[0]) : (bids[0] ?? asks[0]);
    setPrice(best === undefined ? '' : priceBoxText(pair, BigInt(best.price), perOne));
  };
  useEffect(() => {
    if ((price === '' || mode === 'simple') && pair !== undefined) prefill(side);
  }, [pair?.name, open.length, mode, side]);

  const fail = (e: unknown): void => {
    trackError(modal === 'none' ? 'trade' : modal);
    toast.err(e instanceof Error ? e.message : String(e));
  };

  // ---- the ticket -----------------------------------------------------------

  /** The amount as typed, in the base coin's smallest units; null when it does not read. */
  const want = useMemo(() => {
    try {
      return parseAmount(base, normalizeDecimal(amount));
    } catch {
      return null;
    }
  }, [amount, base]);
  /** Simple only: the amount typed is more than is there, so it is cut to what is. */
  const capped = mode === 'simple' && simpleOrder(want, simple).capped;

  const est = useMemo(() => {
    if (pair === undefined || want === null) return null;
    try {
      // Simple: the best price by someone else, and no more than is there,
      // so nothing is left waiting. Advanced: the price and amount typed.
      // The sums are the welcome's own (ticket.ts), so both say the same.
      const s = simpleOrder(want, simple);
      return estimate({
        pair,
        side,
        base: mode === 'simple' ? s.amount : want,
        price: mode === 'simple' ? s.price : priceUnits(),
        // Before the wallet is read, nothing is said to be short.
        balances: wallet === null ? null : wallet.balances,
      });
    } catch {
      return null;
    }
  }, [want, price, pair, side, wallet, mode, simple]);

  // Whether this bid earns the bid share of today's XGE rewards, from the
  // book already on screen: no request of its own.
  const bidHint = useMemo(() => {
    if (pair === undefined || est === null || side !== 'buy' || rewards === null) return null;
    const r = bidReward({
      pair,
      base: est.b,
      price: est.p,
      open,
      trades: activity,
      rules: rewards,
      now: new Date(),
    });
    return r === null
      ? null
      : {
          kind: r.kind,
          text: bidRewardText(r, rewards, (p) => perCoin(pair, p), mode === 'simple'),
        };
  }, [pair, est, side, open, activity, rewards, mode]);
  const bidHintLine =
    bidHint === null ? null : (
      <p className={`wt-reward-hint ${bidHint.kind}`} data-testid="wt-bid-reward">
        <Mark asset="XGE" size={14} />
        <span>{bidHint.text}</span>
      </p>
    );

  const useAll = (): void => {
    if (pair === undefined || wallet === null) return;
    const spend: Asset = side === 'sell' ? base : quote;
    const held = BigInt(wallet.balances[spend] ?? '0');
    const reserve = spend === 'GAJU' ? GAJU_FEE_RESERVE : 0n;
    const usable = held > reserve ? held - reserve : 0n;
    if (side === 'sell') setAmount(inputText(base, usable));
    else {
      const p = mode === 'simple' ? (simple?.price ?? 0n) : priceUnits();
      if (p > 0n) setAmount(inputText(base, (usable * BigInt(pair.priceUnit)) / p));
    }
  };

  const place = async (): Promise<void> => {
    if (pair === undefined || est === null) return;
    setBusy(true);
    try {
      const body = {
        pair: pair.name,
        side,
        baseAmount: est.b.toString(),
        price: est.p.toString(),
      };
      const { id, unsigned } = await api.createOffer(body);
      const finish = async (): Promise<void> => {
        track('order_placed', {
          pair: pair.name,
          side,
          mode,
          ...(draft === null ? {} : { drafted: true }),
        });
        setPlaced(true);
        window.setTimeout(() => {
          setPlaced(false);
        }, 1400);
        toast.ok(
          side === 'buy'
            ? mode === 'simple'
              ? 'Bid placed. You will see it fill here.'
              : 'Bid placed. It fills as offers meet it.'
            : 'Offer placed.',
        );
        setAmount('');
        await load();
      };
      if (unsigned.registerFirst === true) {
        // One PIN covers activating the wallet on this chain and the order.
        setPin({
          unsigned,
          then: async (signed, p) => {
            await api.submitRegister(unsigned.chain, signed);
            const again = await api.createOffer(body);
            if (again.unsigned.registerFirst === true)
              throw new Error('Still activating your wallet on this chain. Try again in a moment.');
            const signedOrder = await signUnsigned(platform, p, again.unsigned);
            await api.submitOffer(again.id, signedOrder);
            await finish();
          },
        });
      } else {
        setPin({
          unsigned,
          then: async (signed) => {
            await api.submitOffer(id, signed);
            await finish();
          },
        });
      }
    } catch (e) {
      fail(e);
    } finally {
      setBusy(false);
    }
  };

  const cancel = async (o: OfferView): Promise<void> => {
    setBusy(true);
    try {
      const { unsigned } = await api.cancelOffer(o.id);
      if (unsigned === null) {
        toast.ok('Cancelled. Nothing was in escrow.');
        await load();
        return;
      }
      setPin({
        unsigned,
        then: async (signed) => {
          await api.submitCancel(o.id, signed);
          toast.ok('Cancelled. Your funds are on their way back.');
          await load();
        },
      });
    } catch (e) {
      fail(e);
    } finally {
      setBusy(false);
    }
  };

  // ---- send and receive -------------------------------------------------------

  const addressOf = (asset: Asset): string | null =>
    wallet === null ? null : asset === 'GAJU' ? wallet.addresses.gajumaru : wallet.addresses.solana;
  const copy = (text: string): void => {
    void navigator.clipboard
      .writeText(text)
      .then(() => {
        toast.ok('Copied');
      })
      .catch(() => {
        toast.err('Could not copy');
      });
  };
  const send = async (): Promise<void> => {
    try {
      const units = parseAmount(modalAsset, normalizeDecimal(sendAmount));
      const { unsigned } = await api.sendPrepare({
        asset: modalAsset,
        to: sendTo.trim(),
        amount: units.toString(),
      });
      setModal('none');
      setPin({
        unsigned,
        then: async (signed) => {
          await api.sendSubmit(unsigned.chain, {
            ...signed,
            asset: modalAsset,
            amount: units.toString(),
          });
          track('send_done', { asset: modalAsset });
          toast.ok(`${amountText(modalAsset, units)} sent.`);
          setSendTo('');
          setSendAmount('');
          await load();
          await loadHistory();
        },
      });
    } catch (e) {
      fail(e);
    }
  };

  // ---- account ------------------------------------------------------------------

  const saveName = async (): Promise<void> => {
    const name = nameDraft.trim();
    if (name === '' || name === me.displayName) return;
    try {
      onMe(await api.updateMe({ displayName: name }));
      toast.ok('Saved');
      setModal('none');
    } catch (e) {
      fail(e);
    }
  };

  // ---- render --------------------------------------------------------------------

  if (!loaded) return <Loading text="Opening the market." />;
  if (pin !== null) {
    return (
      <PinPad
        title="Your PIN"
        hint={`${pin.unsigned.summary}. Signed on this device.`}
        busy={busy}
        onPin={(p) => {
          setBusy(true);
          signUnsigned(platform, p, pin.unsigned)
            .then((signed) => pin.then(signed, p))
            .then(() => {
              setPin(null);
            })
            .catch((e: unknown) => {
              fail(e);
              if (!(e instanceof Error && e.message === 'Wrong PIN')) {
                pin.onCancel?.();
                setPin(null);
              }
            })
            .finally(() => {
              setBusy(false);
            });
        }}
      />
    );
  }

  const supply =
    pair === undefined
      ? null
      : base === 'GAJU'
        ? (market?.gaju.supply ?? null)
        : base === 'XGE'
          ? (market?.xge.supply ?? null)
          : null;
  const cap =
    pair === undefined || supply === null || pair.lastPrice === null || quote !== 'USDC'
      ? null
      : (BigInt(supply) * BigInt(pair.lastPrice)) / BigInt(pair.priceUnit);
  // ---- figures that are only read -------------------------------------------
  // Everything a person reads but never types shows in the currency they chose,
  // with the dollar figure still beside it. Anything they type or sign stays in
  // USDC. With no rate for the currency today, it is all dollars, as before.

  const maxDepth = [...asks, ...bids].reduce(
    (m, o) => (BigInt(o.remaining) > m ? BigInt(o.remaining) : m),
    0n,
  );
  const spread = spreadOf(asks, bids);
  /** The unit the book's Total column reads in: the chosen currency, or the quote. */
  const totalUnit = quote === 'USDC' && readIn !== null ? readIn : assetName[quote];
  const ladder = (rows: OfferView[], kind: 'asks' | 'bids') =>
    rows.slice(0, narrow ? PHONE_LEVELS : 10).map((o) => {
      const total = pair === undefined ? 0n : quoteFor(pair, BigInt(o.remaining), BigInt(o.price));
      const dollars = pair === undefined ? '' : amountText(quote, total);
      const local = quote === 'USDC' ? localOf(Number(total) / 1e6, currency, usdPer) : null;
      return (
        <div
          className={`wt-level ${kind}`}
          key={o.id}
          // A level is three figures wide at most; the dollar total waits here.
          title={[o.mine === 'maker' ? o.statusText : '', local === null ? '' : dollars]
            .filter((t) => t !== '')
            .join(' · ')}
        >
          <span
            className="wt-depth"
            style={{
              width: `${maxDepth === 0n ? 0 : Math.max(4, Number((BigInt(o.remaining) * 100n) / maxDepth))}%`,
            }}
          />
          <span className="wt-price">
            {pair === undefined ? '' : perCoin(pair, o.price)}
            {o.mine === 'maker' ? <span className="wt-mine">YOU</span> : null}
            {o.house ? <span className="wt-mine">XCHANGE</span> : null}
          </span>
          <span className="wt-amt">{bareText(base, BigInt(o.remaining))}</span>
          <span className="wt-total">
            {pair === undefined ? '' : (local ?? bareText(quote, total))}
          </span>
        </div>
      );
    });

  /** What the wallet is worth in dollars at the last trades; null until a price exists. */
  const worth = wallet === null ? null : walletWorth(wallet.balances, pairs);
  /** The same total in the chosen currency, when the day's rates have it. */
  const worthLocal = worth === null ? null : localAmount(worth, currency, usdPer);
  const worthUsd = worth === null ? null : amountText('USDC', worth);
  const walletCard = (
    <div data-testid="wt-balances">
      <div className="wt-card-title">Your wallet</div>
      {
        <div className="wt-fund">
          <div>
            <div className="wt-fund-total" data-testid="wt-worth">
              {wallet === null ? '…' : worth === null ? 'Nothing yet' : (worthLocal ?? worthUsd)}
            </div>
            {worthLocal !== null && (
              <div className="wt-fund-usd" data-testid="wt-worth-usd">
                {worthUsd}
              </div>
            )}
          </div>
          {/* The card route stands beside Add funds whatever the balance: it is
              how most newcomers get their first USDC. */}
          <div className="wt-fund-actions">
            <BuyUsdcButton
              address={addressOf('USDC')}
              from="wallet"
              openLink={(url) => {
                platform.openLink(url);
              }}
              className="wt-submit buy-usdc-cta"
              testId="wt-buy-usdc"
            />
            <button
              className="wt-secondary"
              onClick={() => {
                setModalAsset('USDC');
                track('receive_open', { asset: 'USDC', from: 'wallet' });
                setModal('receive');
              }}
              data-testid="wt-add-funds"
            >
              Add funds
            </button>
          </div>
        </div>
      }
      {(['GAJU', 'USDC', 'XGE'] as Asset[]).map((asset) => (
        <div
          className="wt-bal tap"
          key={asset}
          role="button"
          tabIndex={0}
          onClick={() => {
            setModalAsset(asset);
            setModal('token');
          }}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              setModalAsset(asset);
              setModal('token');
            }
          }}
          data-testid={`wt-token-${asset}`}
        >
          <Mark asset={asset} size={20} />
          <div className="wt-bal-text">
            <span className="wt-bal-name">{assetName[asset]}</span>
            <span className="wt-bal-amt">
              {wallet === null ? '…' : bareText(asset, BigInt(wallet.balances[asset] ?? '0'))}
            </span>
            {asset === 'USDC' &&
              wallet !== null &&
              (() => {
                const line = aboutLocal(BigInt(wallet.balances.USDC ?? '0'), currency, usdPer);
                return line === null ? null : (
                  <span className="wt-bal-local" data-testid="wt-usdc-local">
                    {line}
                  </span>
                );
              })()}
          </div>
          <div
            className="wt-bal-actions"
            onClick={(e) => {
              e.stopPropagation();
            }}
          >
            <button
              onClick={() => {
                setModalAsset(asset);
                track('receive_open', { asset });
                setModal('receive');
              }}
              data-testid={`wt-receive-${asset}`}
            >
              Receive
            </button>
            <button
              onClick={() => {
                setModalAsset(asset);
                setModal('send');
              }}
            >
              Send
            </button>
          </div>
        </div>
      ))}
      <div className="wt-note">
        In your own wallet, on this device. A swap moves only your side into escrow.
      </div>
    </div>
  );

  // ---- getting started ------------------------------------------------------

  const hasFunds =
    wallet !== null && Object.values(wallet.balances).some((b) => b !== null && BigInt(b) > 0n);
  // Recent orders come with the book; the rest is in the wallet's history.
  const hasTraded =
    mine.some((o) => o.fills.some((f) => f.status === 'done')) ||
    history.some((h) => h.kind === 'bought' || h.kind === 'sold');
  const hasOrdered =
    hasTraded ||
    mine.some((o) => o.mine === 'maker') ||
    history.some((h) => h.kind === 'offer' || h.kind === 'cancelled' || h.kind === 'expired');
  const steps: {
    what: string;
    why: string;
    done: boolean;
    action?: () => void;
    label?: string;
    /** A second way to do this step, offered beside the first. */
    extra?: ReactNode;
  }[] = [
    { what: 'Wallet made', why: '', done: true },
    {
      what: 'Words written down',
      why: '',
      done: !needsConfirmation(),
    },
    {
      what: 'Add funds',
      why: 'With a bank card, or send to your address.',
      done: hasFunds,
      action: () => {
        setModalAsset('USDC');
        track('receive_open', { asset: 'USDC', from: 'steps' });
        setModal('receive');
      },
      label: 'Receive',
      extra: (
        <BuyUsdcButton
          address={addressOf('USDC')}
          from="steps"
          openLink={(url) => {
            platform.openLink(url);
          }}
          className="buy-usdc-cta"
          testId="wt-step-buy-usdc"
        />
      ),
    },
    { what: 'Place your first order', why: '', done: hasOrdered },
    { what: 'First swap', why: '', done: hasTraded },
  ];
  const doneCount = steps.filter((st) => st.done).length;
  const nextIndex = steps.findIndex((st) => !st.done);
  const startCard =
    startDismissed || doneCount === steps.length ? null : (
      <div
        className={`wt-card wt-start${!narrow || stepsOpen ? ' open' : ''}`}
        data-testid="wt-start"
      >
        <div className="wt-card-title">
          Getting started
          <span className="wt-card-sub">
            {doneCount} of {steps.length}
            {' · '}
            {narrow && (
              <>
                <button
                  className="wt-link"
                  onClick={() => {
                    setStepsOpen(!stepsOpen);
                  }}
                  aria-expanded={stepsOpen}
                  data-testid="wt-steps-all"
                >
                  {stepsOpen ? 'Less' : 'All steps'}
                </button>
                {' · '}
              </>
            )}
            <button
              className="wt-link"
              onClick={() => {
                setStartDismissed(true);
                track('start_hidden', { done: doneCount });
                try {
                  localStorage.setItem('xchange.start.done', '1');
                } catch {
                  /* fine */
                }
              }}
            >
              Hide
            </button>
          </span>
        </div>
        <div className="wt-progress">
          <span style={{ width: `${(doneCount / steps.length) * 100}%` }} />
        </div>
        <div className="wt-steps">
          {steps.map((st, i) => (
            <div
              className={`wt-step${st.done ? ' done' : i === nextIndex ? ' next' : ''}`}
              key={st.what}
            >
              <span className="mark">{st.done ? '✓' : i + 1}</span>
              <span className="what">
                {st.what}
                {st.why !== '' && <span className="why">{st.why}</span>}
              </span>
              {!st.done && (st.extra ?? null)}
              {!st.done && st.action !== undefined && (
                <button onClick={st.action}>{st.label}</button>
              )}
            </div>
          ))}
        </div>
        <SharedLine line={market?.rewards?.sharedLine ?? null} className="wt-start-shared" />
      </div>
    );

  // ---- simple ticket --------------------------------------------------------

  /** "1,900 XGE", "1,195 Gaju": whole amounts without ".00". */
  const qtyText = (units: bigint): string => amountText(base, units).replace(/\.00 /, ' ');
  const toAdvanced = (
    <button
      className="wt-link"
      type="button"
      onClick={() => {
        setMode('pro');
      }}
      data-testid="wt-to-advanced"
    >
      Advanced
    </button>
  );
  /** What the order needs that the wallet does not have: all of it in a new wallet. */
  // "You'll need $4.21 of USDC", "You'll need 100,000.01 Gaju"; "more" once something is there.
  const shortBy = ((): string | null => {
    if (est === null || !est.short || est.held === null) return null;
    const some = est.held > 0n;
    const text = amountText(est.spend, some ? est.need - est.held : est.need);
    if (est.spend === 'USDC') return `You'll need ${text} ${some ? 'more' : 'of'} USDC`;
    return `You'll need ${text}${some ? ' more' : ''}`;
  })();
  const simpleTicket = (
    <SimpleTicket
      pair={pair}
      side={side}
      onSide={setSide}
      amount={amount}
      onAmount={setAmount}
      simple={simple}
      est={est}
      capped={capped}
      currency={currency}
      rates={usdPer}
      onMax={useAll}
      toAdvanced={toAdvanced}
      hint={bidHintLine}
      action={
        simple === null ? null : (
          <>
            <button
              className={`wt-submit ${side}${placed ? ' placed' : ''}`}
              aria-busy={busy}
              disabled={busy || est === null || est.short || !pair?.live}
              onClick={() => {
                void place();
              }}
              data-testid="wt-place"
            >
              {pair !== undefined && !pair.live
                ? 'Opening soon'
                : (shortBy ??
                  `${side === 'buy' ? 'Buy' : 'Sell'} ${capped && est !== null ? qtyText(est.b) : assetName[base]}`)}
            </button>
            {/* Refusing is half an answer: the way to get what it needs is
                offered here. USDC by bank card or a send; Gaju and XGE by a
                send to this wallet's own address. */}
            {est?.short === true && (
              <div className="wt-no-usdc" data-testid="wt-no-funds">
                {est.spend === 'USDC' ? (
                  <>
                    <p className="wt-note" data-testid="wt-no-usdc">
                      You need USDC in this wallet to buy {assetName[base]}. You can buy some with a
                      bank card, or send USDC you already have to your Solana address.
                    </p>
                    <BuyUsdcButton
                      address={addressOf('USDC')}
                      from="ticket"
                      openLink={(url) => {
                        platform.openLink(url);
                      }}
                      className="wt-submit buy-usdc-cta"
                      testId="wt-ticket-buy-usdc"
                    />
                  </>
                ) : (
                  <>
                    <p className="wt-note">
                      {est.spend === 'GAJU'
                        ? 'Send Gaju to this wallet from any Gaju wallet. It shows here when it lands, and the ticket stays as it is.'
                        : 'Send XGE to this wallet from any Solana wallet. It shows here when it lands, and the ticket stays as it is.'}
                    </p>
                    <button
                      type="button"
                      className="wt-submit wt-receive-cta"
                      onClick={() => {
                        setModalAsset(est.spend);
                        track('receive_open', { asset: est.spend, from: 'ticket' });
                        setModal('receive');
                      }}
                      data-testid="wt-ticket-receive"
                    >
                      Receive {assetName[est.spend]}
                    </button>
                  </>
                )}
              </div>
            )}
          </>
        )
      }
    />
  );

  // ---- from Earn to the ticket ---------------------------------------------------

  /** Advanced, on the pair, Buy, with the bid Earn worked out typed in. */
  const bidFromEarn = (preset: BidPreset | null, name: string | null): void => {
    const target = pairs.find((x) => x.name === (preset?.pair ?? name));
    setMode('pro');
    if (target !== undefined && target.name !== pairName) {
      clearDraft();
      setPairName(target.name);
    }
    setSideState('buy');
    if (preset === null || target === undefined) {
      setPrice('');
      return;
    }
    const hadBid = mine.some(
      (o) =>
        o.mine === 'maker' && o.side === 'buy' && o.pair === target.name && !finished(o.status),
    );
    setPrice(priceBoxText(target, preset.price, target.base === 'GAJU'));
    setAmountState(inputText(target.base as Asset, preset.amount));
    toast.ok(
      hadBid
        ? 'A bid 10% under the best offer is filled in. Place it, then cancel the old one.'
        : 'A bid 10% under the best offer is filled in.',
    );
  };

  // ---- the referral link, shared from Account --------------------------------

  const copyLink = (link: string, from: string): void => {
    track('referral_copy', { from });
    void navigator.clipboard
      .writeText(link)
      .then(() => {
        toast.ok('Link copied. Paste it anywhere.');
      })
      .catch(() => {
        toast.err('Could not copy. Select the link above.');
      });
  };

  return (
    <div className="wt" data-testid="web-trade">
      <div className="wt-bar">
        <div className="wt-pairs" role="tablist" aria-label="Pairs">
          {pairs.map((p) => (
            <button
              key={p.name}
              role="tab"
              aria-selected={p.name === pair?.name}
              onClick={() => {
                if (p.name !== pair?.name) clearDraft();
                setPairName(p.name);
                setPrice('');
              }}
              data-testid={`wt-pair-${p.name}`}
            >
              <Mark asset={p.logo} size={16} /> {p.display}
            </button>
          ))}
        </div>
        {/* All four figures at once; on a phone they sit two by two. */}
        <div className="wt-stats">
          <Stat
            id="wt-stat-last"
            label="Last"
            value={
              pair?.lastPrice
                ? read(perCoinUsd(pair, pair.lastPrice), perCoin(pair, pair.lastPrice))
                : 'No trade'
            }
          />
          <Stat
            id="wt-stat-trades"
            label="24h trades"
            value={pair === undefined || pair.trades24h === 0 ? '' : String(pair.trades24h)}
          />
          <Stat
            id="wt-stat-volume"
            label="24h volume"
            value={
              pair === undefined || pair.trades24h === 0
                ? ''
                : readAmount(quote, BigInt(pair.volume24h))
            }
          />
          <Stat
            id="wt-stat-cap"
            label="Market cap"
            value={cap === null ? '' : read(Number(cap) / 1e6, compactUsd(cap), true)}
          />
        </div>
        <div className="wt-me">
          <div className="wt-mode three" role="tablist" aria-label="View">
            {/* Earn first; the app still opens on Simple or Advanced. */}
            <button
              role="tab"
              aria-selected={mode === 'earn'}
              onClick={() => {
                setMode('earn');
              }}
              data-testid="mode-earn"
            >
              Earn
            </button>
            <button
              role="tab"
              aria-selected={mode === 'simple'}
              onClick={() => {
                setMode('simple');
              }}
              data-testid="mode-simple"
            >
              Simple
            </button>
            <button
              role="tab"
              aria-selected={mode === 'pro'}
              onClick={() => {
                setMode('pro');
              }}
              data-testid="mode-pro"
            >
              Advanced
            </button>
          </div>
          <button
            className="wt-chip wt-chip-wallet"
            onClick={() => {
              track('wallet_open');
              setModal('wallet');
            }}
            data-testid="wt-wallet"
          >
            Wallet
            {worth !== null && wallet !== null && (
              <span className="wt-chip-sub">
                {worthLocal ?? worthUsd}
                {worthLocal !== null && <span className="wt-chip-usd">{worthUsd}</span>}
              </span>
            )}
          </button>
          <button
            className="wt-chip"
            onClick={() => {
              track('account_open');
              setForgetId(null);
              setModal('account');
            }}
            data-testid="wt-account"
          >
            Account
          </button>
        </div>
      </div>

      {mode === 'earn' ? (
        <div className="wt-simple" data-testid="wt-earn">
          <Earn
            pairs={pairs}
            wordsDone={!needsConfirmation()}
            funded={hasFunds}
            onBid={bidFromEarn}
          />
        </div>
      ) : mode === 'simple' ? (
        <div className="wt-simple" data-testid="wt-simple">
          {startCard}
          {simpleTicket}
        </div>
      ) : (
        <div className="wt-grid">
          <section className="wt-col wt-left" data-testid="wt-latest">
            <div className="wt-card">
              <div className="wt-card-title">Latest trades</div>
              {trades.length === 0 ? (
                <div className="wt-empty">No trades yet on this pair.</div>
              ) : (
                trades.map((a) => {
                  const px =
                    pair === undefined
                      ? 0n
                      : (BigInt(a.quoteAmount) * BigInt(pair.priceUnit)) / BigInt(a.baseAmount);
                  return (
                    <div className={`wt-trade ${a.side}`} key={a.id}>
                      <span className="wt-trade-amt">{bareText(base, BigInt(a.baseAmount))}</span>
                      <span className="wt-trade-px">
                        {pair === undefined ? '' : read(perCoinUsd(pair, px), perCoin(pair, px))}
                      </span>
                      <span className="wt-trade-when">{ago(a.at)}</span>
                    </div>
                  );
                })
              )}
            </div>
          </section>

          <section className="wt-col wt-book" data-testid="wt-book">
            <div className="wt-card wt-book-card">
              <div className="wt-card-title">
                Order book
                <span className="wt-card-sub">
                  {asks.length} {asks.length === 1 ? 'offer' : 'offers'} · {bids.length}{' '}
                  {bids.length === 1 ? 'bid' : 'bids'}
                </span>
              </div>
              <div className="wt-level head">
                <span className="wt-price" data-testid="wt-book-price-head">
                  Price ({quote === 'USDC' ? '$' : assetName[quote]})
                </span>
                <span className="wt-amt">Amount ({assetName[base]})</span>
                <span className="wt-total" data-testid="wt-book-total-head">
                  Total ({totalUnit})
                </span>
              </div>
              <div className="wt-book-note">
                <span>Selling {assetName[base]}: lowest price nearest the middle</span>
              </div>
              <div className="wt-asks">
                {asks.length === 0 ? (
                  <div className="wt-empty">Nobody is selling yet.</div>
                ) : (
                  ladder(asks, 'asks')
                )}
              </div>
              <div className="wt-spread">
                {spread === null
                  ? 'Be the first on one side'
                  : spread <= 0n
                    ? 'Matching now'
                    : `Spread ${pair === undefined ? '' : perCoin(pair, spread)}`}
              </div>
              <div className="wt-bids">
                {bids.length === 0 ? (
                  <div className="wt-empty">Nobody is buying yet.</div>
                ) : (
                  ladder(bids, 'bids')
                )}
              </div>
              <div className="wt-book-note">
                <span>Buying {assetName[base]}: highest price nearest the middle</span>
              </div>
            </div>
          </section>

          <section className="wt-col wt-ticket" data-testid="wt-ticket">
            <div className="wt-card">
              <div className="wt-seg" role="tablist">
                <button
                  role="tab"
                  aria-selected={side === 'buy'}
                  className="buy"
                  onClick={() => {
                    setSide('buy');
                    prefill('buy');
                  }}
                  data-testid="wt-buy"
                >
                  Buy {assetName[base]}
                </button>
                <button
                  role="tab"
                  aria-selected={side === 'sell'}
                  className="sell"
                  onClick={() => {
                    setSide('sell');
                    prefill('sell');
                  }}
                  data-testid="wt-sell"
                >
                  Sell {assetName[base]}
                </button>
              </div>
              <label className="wt-field">
                <span className="wt-field-label">
                  Amount
                  <button className="wt-max" onClick={useAll} type="button">
                    Max
                  </button>
                </span>
                <span className="wt-input">
                  <input
                    inputMode="decimal"
                    value={amount}
                    placeholder="0"
                    onChange={(e) => {
                      setAmount(e.target.value);
                    }}
                    data-testid="wt-amount"
                  />
                  <span className="wt-unit">{assetName[base]}</span>
                </span>
              </label>
              <label className="wt-field">
                <span className="wt-field-label">
                  Price per {assetName[base]}
                  {pair !== undefined && (
                    <button
                      className="wt-max"
                      type="button"
                      onClick={() => {
                        prefill(side);
                      }}
                    >
                      Market
                    </button>
                  )}
                </span>
                <span className="wt-input">
                  <input
                    inputMode="decimal"
                    value={price}
                    placeholder="0"
                    onChange={(e) => {
                      setPrice(e.target.value);
                    }}
                    data-testid="wt-price"
                  />
                  <span className="wt-unit">{quote === 'USDC' ? '$' : assetName[quote]}</span>
                </span>
                {/* The price is typed, so it stays in dollars, and reads out
                    in the chosen currency under the box. */}
                {quote === 'USDC' &&
                  est !== null &&
                  pair !== undefined &&
                  (() => {
                    const text = localOf(perCoinUsd(pair, est.p), currency, usdPer);
                    return text === null ? null : (
                      <span className="wt-field-about" data-testid="wt-price-local">
                        about {text} each
                      </span>
                    );
                  })()}
              </label>
              <div className="wt-sum">
                <div>
                  <span>{side === 'buy' ? 'You pay at most' : 'You get at least'}</span>
                  <strong data-testid="wt-total">
                    {est === null || pair === undefined
                      ? ''
                      : amountText(quote, side === 'sell' ? netOfFee(pair, est.q) : est.q)}
                  </strong>
                </div>
                {/* The total is the figure that gets signed, so it stays in USDC;
                    this only reads it out in the chosen currency beneath. */}
                {quote === 'USDC' &&
                  est !== null &&
                  pair !== undefined &&
                  (() => {
                    const text = localAmount(
                      side === 'sell' ? netOfFee(pair, est.q) : est.q,
                      currency,
                      usdPer,
                    );
                    return text === null ? null : (
                      <div className="wt-sum-local" data-testid="wt-total-local">
                        about {text}
                      </div>
                    );
                  })()}
                <div className="wt-sum-sub">
                  {pair === undefined
                    ? ''
                    : side === 'buy'
                      ? est === null
                        ? `${pair.feeBps / 100}% fee.`
                        : `You receive ${amountText(base, netOfFee(pair, est.b))} after the ${pair.feeBps / 100}% fee.`
                      : `After the ${pair.feeBps / 100}% fee.`}
                  {est !== null && est.fee > 0n
                    ? ` Plus ${amountText(est.spend, est.fee)} network.`
                    : ''}
                </div>
              </div>
              {bidHintLine}
              <button
                className={`wt-submit ${side}${placed ? ' placed' : ''}`}
                aria-busy={busy}
                disabled={busy || est === null || est.short || !pair?.live}
                onClick={() => {
                  void place();
                }}
                data-testid="wt-place"
              >
                {pair !== undefined && !pair.live
                  ? 'Opening soon'
                  : est?.short === true
                    ? `Not enough ${assetName[est.spend]}`
                    : side === 'buy'
                      ? 'Place bid'
                      : 'Place offer'}
              </button>
              <div className="wt-note">
                Your side goes into escrow on its chain, signed by you. It fills as the other side
                arrives, or comes back when you cancel.
              </div>
            </div>
          </section>
        </div>
      )}

      <section className="wt-below">
        <div className="wt-tabs" role="tablist">
          <button
            role="tab"
            aria-selected={below === 'orders'}
            onClick={() => {
              setBelow('orders');
            }}
          >
            Open orders{orders.length > 0 ? ` · ${orders.length}` : ''}
          </button>
          <button
            role="tab"
            aria-selected={below === 'history'}
            onClick={() => {
              setBelow('history');
              if (!historyOpen.current) {
                historyOpen.current = true;
                void loadEnded().catch(() => undefined);
              }
            }}
          >
            History
          </button>
          {below === 'history' && ended !== null && (
            <button
              className="wt-link wt-tabs-end"
              onClick={() => {
                void loadEnded().catch(() => undefined);
              }}
              data-testid="wt-history-refresh"
            >
              Refresh
            </button>
          )}
        </div>
        {below === 'orders' ? (
          orders.length === 0 ? (
            <div className="wt-empty">No open orders.</div>
          ) : (
            <div className="wt-scroll">
              <table className="wt-table" data-testid="wt-orders">
                <thead>
                  <tr>
                    <th>Pair</th>
                    <th>Side</th>
                    <th className="num">Price</th>
                    <th className="num">Remaining</th>
                    <th className="st">Status</th>
                    <th />
                  </tr>
                </thead>
                <tbody>
                  {orders.map((o) => {
                    const p = pairs.find((x) => x.name === o.pair);
                    return (
                      // On a phone the Status column gives way; it is here instead.
                      <tr key={o.id} title={o.statusText}>
                        <td>{p?.display ?? o.pair}</td>
                        <td className={o.side === 'buy' ? 'up' : 'down'}>
                          {o.side === 'buy' ? 'Bid' : 'Offer'}
                        </td>
                        <td className="num">
                          {p === undefined
                            ? o.price
                            : read(perCoinUsd(p, o.price), perCoin(p, o.price))}
                        </td>
                        <td className="num">
                          {p === undefined
                            ? o.remaining
                            : bareText(p.base as Asset, BigInt(o.remaining))}
                        </td>
                        <td className="muted st">{o.statusText}</td>
                        <td className="act">
                          {o.cancellable && (
                            <button
                              className="wt-link danger"
                              onClick={() => {
                                void cancel(o);
                              }}
                            >
                              Cancel
                            </button>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )
        ) : ended === null ? (
          <div className="wt-empty">Loading…</div>
        ) : historyRows.length === 0 ? (
          <div className="wt-empty">Nothing yet. Trades, sends and rewards appear here.</div>
        ) : (
          <div className="wt-scroll">
            <table className="wt-table" data-testid="wt-history">
              <thead>
                <tr>
                  <th>When</th>
                  <th>What</th>
                  <th className="num">Amount</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {historyRows.map((h) => (
                  <tr key={h.key} title={new Date(h.at).toLocaleString()}>
                    <td className="muted">
                      {narrow ? ago(h.at) : new Date(h.at).toLocaleString()}
                    </td>
                    <td>{h.text}</td>
                    <td className={`num ${h.sign === '-' ? 'down' : h.sign === '+' ? 'up' : ''}`}>
                      {/* A dollar amount reads in the chosen currency, with the
                          dollars beneath; Gaju and XGE keep their own units. */}
                      {(() => {
                        const local = h.usd === null ? null : localOf(h.usd, currency, usdPer);
                        return local === null ? (
                          h.amount
                        ) : (
                          <>
                            {h.sign === '-' ? '−' : h.sign === '+' ? '+' : ''}
                            {local}
                            <span className="wt-usd">{h.amount}</span>
                          </>
                        );
                      })()}
                    </td>
                    <td className="act">
                      {h.url !== null && (
                        <a className="wt-link" href={h.url} target="_blank" rel="noreferrer">
                          On chain
                        </a>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>

      {modal !== 'none' && (
        <div
          className="wt-modal-back"
          onClick={() => {
            setPhrase(null);
            setForgetId(null);
            setModal('none');
          }}
        >
          <div
            className="wt-modal"
            onClick={(e) => {
              e.stopPropagation();
            }}
            role="dialog"
          >
            <button
              className="wt-modal-close"
              aria-label="Close"
              onClick={() => {
                setPhrase(null);
                setForgetId(null);
                setModal('none');
              }}
              data-testid="modal-close"
            >
              ×
            </button>
            {modal === 'wallet' && walletCard}
            {modal === 'token' && (
              <>
                <div className="wt-card-title">
                  <span className="wt-token-head">
                    <Mark asset={modalAsset} size={22} /> {assetName[modalAsset]}
                  </span>
                  <span className="wt-card-sub">
                    {wallet === null
                      ? ''
                      : bareText(modalAsset, BigInt(wallet.balances[modalAsset] ?? '0'))}
                  </span>
                </div>
                <div className="wt-token-actions">
                  <button
                    className="wt-secondary"
                    onClick={() => {
                      setModal('receive');
                    }}
                  >
                    Receive
                  </button>
                  <button
                    className="wt-secondary"
                    onClick={() => {
                      setModal('send');
                    }}
                  >
                    Send
                  </button>
                </div>
                <div className="wt-card-title" style={{ marginTop: 6 }}>
                  What happened
                </div>
                {history.filter((h) => h.asset === modalAsset).length === 0 ? (
                  <div className="wt-empty">Nothing yet for {assetName[modalAsset]}.</div>
                ) : (
                  <div className="wt-token-list" data-testid="wt-token-history">
                    {history
                      .filter((h) => h.asset === modalAsset)
                      .slice(0, 60)
                      .map((h) => (
                        <div className="wt-token-row" key={`${h.asset}:${h.id}`}>
                          <div className="wt-token-text">
                            <span>{h.text}</span>
                            <span className="wt-token-when">
                              {new Date(h.at).toLocaleString()}
                              {h.url !== null ? (
                                <>
                                  {' · '}
                                  <a
                                    className="wt-link"
                                    href={h.url}
                                    target="_blank"
                                    rel="noreferrer"
                                  >
                                    On chain
                                  </a>
                                </>
                              ) : null}
                            </span>
                          </div>
                          <span
                            className={`wt-token-amt ${h.amount.startsWith('-') ? 'down' : 'up'}`}
                          >
                            {h.amount.startsWith('-') ? '−' : '+'}
                            {bareText(h.asset, BigInt(h.amount.replace('-', '')))}
                          </span>
                        </div>
                      ))}
                  </div>
                )}
                <button
                  className="wt-secondary"
                  onClick={() => {
                    setModal('wallet');
                  }}
                >
                  Close
                </button>
              </>
            )}
            {modal === 'receive' && (
              <>
                <div className="wt-card-title">Add funds</div>
                <div className="wt-seg three" role="tablist">
                  {(['USDC', 'GAJU', 'XGE'] as Asset[]).map((a) => (
                    <button
                      role="tab"
                      key={a}
                      aria-selected={modalAsset === a}
                      className="plain"
                      onClick={() => {
                        setModalAsset(a);
                      }}
                      data-testid={`receive-${a}`}
                    >
                      {assetName[a]}
                    </button>
                  ))}
                </div>
                {/* The first choice for USDC: a card. Sending from another
                    wallet is below it, for those who already hold some. */}
                {modalAsset === 'USDC' && (
                  <BuyUsdc
                    address={addressOf('USDC')}
                    copy={copy}
                    from="add-funds"
                    openLink={(url) => {
                      platform.openLink(url);
                    }}
                  />
                )}
                {modalAsset === 'USDC' && (
                  <div className="wt-card-title small" data-testid="wt-or-send">
                    Or send USDC you already have
                  </div>
                )}
                <p className="wt-note">
                  {modalAsset === 'USDC'
                    ? 'USDC on Solana only; other networks do not arrive.'
                    : `Your ${modalAsset === 'GAJU' ? 'Gaju' : 'Solana'} address.`}
                </p>
                <code className="wt-addr" data-testid="wt-address">
                  {addressOf(modalAsset) ?? '…'}
                </code>
                <button
                  className="wt-submit"
                  onClick={() => {
                    const a = addressOf(modalAsset);
                    if (a !== null) copy(a);
                  }}
                >
                  Copy address
                </button>
                <p className="wt-note">It shows up within a minute of landing.</p>
                {/* Only under the Vite dev server (pnpm try): coins on the
                    fake chain, so a change can be tried end to end. The
                    production build has no such button. */}
                {import.meta.env.DEV && modalAsset !== 'XGE' && (
                  <button
                    className="wt-secondary"
                    onClick={() => {
                      void fetch('/api/dev/fund-wallet', {
                        method: 'POST',
                        credentials: 'include',
                        headers: { 'content-type': 'application/json' },
                        body: JSON.stringify({
                          asset: modalAsset,
                          amount: modalAsset === 'USDC' ? '100000000' : '1000000000000000000000000',
                        }),
                      }).then((r) => {
                        if (r.ok) toast.ok(`Test ${assetName[modalAsset]} is on its way.`);
                        else toast.err('Test coins work only on the fake chain.');
                      });
                    }}
                    data-testid="dev-test-coins"
                  >
                    Test coins ({modalAsset === 'USDC' ? '$100' : '1,000,000 Gaju'})
                  </button>
                )}
                <button
                  className="wt-secondary"
                  onClick={() => {
                    setModal('wallet');
                  }}
                  data-testid="receive-close"
                >
                  Close
                </button>
              </>
            )}
            {modal === 'send' && (
              <>
                <div className="wt-card-title">Send {assetName[modalAsset]}</div>
                <label className="wt-field">
                  <span className="wt-field-label">To</span>
                  <span className="wt-input">
                    <input
                      value={sendTo}
                      placeholder={modalAsset === 'GAJU' ? 'ak_…' : 'Solana address'}
                      onChange={(e) => {
                        setSendTo(e.target.value);
                      }}
                    />
                  </span>
                </label>
                <label className="wt-field">
                  <span className="wt-field-label">Amount</span>
                  <span className="wt-input">
                    <input
                      inputMode="decimal"
                      value={sendAmount}
                      placeholder="0"
                      onChange={(e) => {
                        setSendAmount(e.target.value);
                      }}
                    />
                    <span className="wt-unit">{assetName[modalAsset]}</span>
                  </span>
                </label>
                <button
                  className="wt-submit"
                  disabled={sendTo.trim() === '' || sendAmount.trim() === ''}
                  onClick={() => {
                    void send();
                  }}
                >
                  Continue
                </button>
                <button
                  className="wt-secondary"
                  onClick={() => {
                    setModal('none');
                  }}
                  data-testid="send-cancel"
                >
                  Cancel
                </button>
              </>
            )}
            {modal === 'account' && (
              <>
                <div className="wt-card-title">Your account</div>
                {rewards !== null && (
                  <div className="wt-rewards" data-testid="wt-rewards">
                    <div className="wt-card-title">
                      XGE rewards
                      <span className="wt-card-sub">
                        in this wallet: {xgeText(rewards.inWallet)} XGE
                      </span>
                    </div>
                    <p className="wt-note">
                      {BigInt(rewards.accrued) > 0n
                        ? `${xgeText(rewards.accrued)} XGE accrued, paid after midnight UTC.`
                        : 'Paid after midnight UTC.'}{' '}
                      Today you swapped {`$${(Number(rewards.myVolumeTodayUsd) / 1e6).toFixed(2)}`}{' '}
                      of {`$${(Number(rewards.todayVolumeUsd) / 1e6).toFixed(2)}`}.
                    </p>
                    <div className="wt-referral" data-testid="wt-referral">
                      <p className="wt-note">{referralLine(rewards)}</p>
                      <p className="wt-ref-link" data-testid="wt-ref-link">
                        {rewards.refWebLink}
                      </p>
                      <button
                        className="wt-secondary"
                        onClick={() => {
                          copyLink(rewards.refWebLink, 'account');
                        }}
                        data-testid="wt-copy-link"
                      >
                        Copy my link
                      </button>
                      <div className="share-row">
                        {shareTargets(rewards.refWebLink, inviteText(rewards)).map(
                          ([name, url]) => (
                            <button
                              className="wt-secondary"
                              key={name}
                              onClick={() => {
                                track('referral_share', { to: name.toLowerCase() });
                                platform.openLink(url);
                              }}
                              data-testid={`wt-share-${name.toLowerCase()}`}
                            >
                              {name}
                            </button>
                          ),
                        )}
                      </div>
                    </div>
                  </div>
                )}
                <label className="wt-field">
                  <span className="wt-field-label">Your name</span>
                  <span className="wt-input">
                    <input
                      value={nameDraft}
                      maxLength={32}
                      onChange={(e) => {
                        setNameDraft(e.target.value);
                      }}
                      data-testid="name-input"
                    />
                  </span>
                </label>
                <button
                  className="wt-submit"
                  disabled={nameDraft.trim() === '' || nameDraft.trim() === me.displayName}
                  onClick={() => {
                    void saveName();
                  }}
                  data-testid="name-save"
                >
                  Save name
                </button>
                <p className="wt-note">Shown on Earn's board when your bid counts.</p>
                <button
                  className="wt-secondary"
                  onClick={() => {
                    setModal('phrase');
                  }}
                  data-testid="show-phrase"
                >
                  Show recovery phrase
                </button>
                <p className="wt-note">Your PIN opens this device. Your twelve words open any.</p>
                <div className="wt-card-title" style={{ marginTop: 14 }}>
                  Wallets on this device
                </div>
                <div className="wt-wallets" data-testid="wt-wallets">
                  {listWallets().map((w) => (
                    <div className="wt-wallet-row" key={w.id}>
                      <button
                        className="wt-step"
                        disabled={w.id === activeWalletId()}
                        onClick={() => {
                          if (!switchWallet(w.id)) return;
                          track('wallet_switch');
                          void api
                            .logout()
                            .catch(() => undefined)
                            .then(() => {
                              window.location.reload();
                            });
                        }}
                      >
                        <span className="mark">{w.id === activeWalletId() ? '✓' : ''}</span>
                        <span className="what">
                          {w.label}
                          <span className="why">
                            {w.id === activeWalletId() ? 'Open now' : 'Switch: its PIN opens it'}
                          </span>
                        </span>
                      </button>
                      <button
                        className="wt-wallet-remove"
                        onClick={() => {
                          setForgetId(w.id);
                          setModal('forget');
                        }}
                        data-testid="forget-wallet"
                      >
                        Remove from this device
                      </button>
                    </div>
                  ))}
                  <button
                    className="wt-secondary"
                    onClick={() => {
                      startAnotherWallet();
                      track('wallet_add');
                      void api
                        .logout()
                        .catch(() => undefined)
                        .then(() => {
                          window.location.reload();
                        });
                    }}
                    data-testid="wt-add-wallet"
                  >
                    Add another wallet
                  </button>
                </div>
                <label className="wt-field" style={{ marginTop: 14 }}>
                  <span className="wt-field-label">USDC in your money</span>
                  <span className="wt-input">
                    <select
                      className="wt-select"
                      value={currency ?? 'off'}
                      onChange={(e) => {
                        money.choose(e.target.value);
                      }}
                      data-testid="local-currency"
                    >
                      <option value="off">Do not show</option>
                      {CURRENCIES.map((c) => (
                        <option key={c} value={c}>
                          {currencyName(c)} ({c})
                        </option>
                      ))}
                    </select>
                  </span>
                </label>
                <p className="wt-note">
                  A rough figure under your USDC balance, from the European Central Bank's daily
                  rates.
                  {currency !== null &&
                    usdPer !== null &&
                    (() => {
                      const held = BigInt(wallet?.balances.USDC ?? '0');
                      const line = aboutLocal(held > 0n ? held : 1_000_000n, currency, usdPer);
                      return line === null ? null : (
                        <>
                          {' '}
                          {held > 0n ? 'Your USDC: ' : '1 USDC: '}
                          <span data-testid="local-currency-preview">{line}</span>.
                        </>
                      );
                    })()}
                </p>
              </>
            )}
            {modal === 'phrase' &&
              (phrase === null ? (
                <PhrasePin
                  platform={platform}
                  walletId={forgetId ?? undefined}
                  onPhrase={setPhrase}
                  onBack={() => {
                    setModal(forgetId === null ? 'account' : 'forget');
                  }}
                />
              ) : (
                <>
                  <div className="wt-card-title">Your recovery phrase</div>
                  <p className="wt-note">
                    These twelve words open your wallet on any device. Keep them safe and private.
                  </p>
                  <code className="wt-addr" data-testid="phrase">
                    {phrase}
                  </code>
                  <button
                    className="wt-submit"
                    onClick={() => {
                      setPhrase(null);
                      setModal(forgetId === null ? 'none' : 'forget');
                    }}
                    data-testid="phrase-close"
                  >
                    Done
                  </button>
                </>
              ))}
            {modal === 'forget' &&
              (() => {
                const entry = listWallets().find((w) => w.id === forgetId);
                if (entry === undefined) return null;
                const isOpen = entry.id === activeWalletId();
                const seen = isOpen
                  ? wallet === null
                    ? null
                    : {
                        gaju: wallet.balances.GAJU,
                        usdc: wallet.balances.USDC,
                        xge: wallet.balances.XGE,
                        worth: worth === null ? null : worth.toString(),
                        orders: orders.length,
                        at: new Date().toISOString(),
                      }
                  : walletSeen(entry.id);
                return (
                  <ForgetWallet
                    key={entry.id}
                    platform={platform}
                    entry={entry}
                    isOpen={isOpen}
                    address={isOpen ? (wallet?.addresses.solana ?? entry.solana) : entry.solana}
                    seen={seen}
                    live={isOpen}
                    onShowWords={() => {
                      setModal('phrase');
                    }}
                    onKeep={() => {
                      setForgetId(null);
                      setModal('account');
                    }}
                    onRemoved={({ wasOpen }) => {
                      setForgetId(null);
                      if (wasOpen) {
                        // Signed out of it: the next wallet's PIN, or the welcome.
                        void api
                          .logout()
                          .catch(() => undefined)
                          .then(() => {
                            window.location.reload();
                          });
                        return;
                      }
                      toast.ok('Removed from this device');
                      setWalletsTick((n) => n + 1);
                      setModal('account');
                    }}
                  />
                );
              })()}
          </div>
        </div>
      )}
    </div>
  );
}

function Stat({ label, value, id }: { label: string; value: ReactNode; id: string }) {
  return (
    <div className="wt-stat" data-testid={id}>
      <span className="wt-stat-label">{label}</span>
      <span className="wt-stat-value">{value}</span>
    </div>
  );
}

/** The PIN, then the words; a wrong PIN says so and stays. */
function PhrasePin({
  platform,
  walletId,
  onPhrase,
  onBack,
}: {
  platform: Platform;
  /** Another wallet on this device, not the open one. */
  walletId?: string | undefined;
  onPhrase: (phrase: string) => void;
  onBack: () => void;
}) {
  const [busy, setBusy] = useState(false);
  return (
    <>
      <div className="wt-card-title">Your PIN</div>
      <p className="wt-note">To show your recovery phrase.</p>
      <PinPad
        title=""
        busy={busy}
        onPin={(p) => {
          setBusy(true);
          withWallet(platform, p, (w) => w.phrase, walletId)
            .then(onPhrase)
            .catch((e: unknown) => {
              toast.err(e instanceof Error ? e.message : String(e));
              if (!(e instanceof Error && e.message === 'Wrong PIN')) onBack();
            })
            .finally(() => {
              setBusy(false);
            });
        }}
      />
    </>
  );
}

/** $1.2K, $130M, $1.2T. */
function compactUsd(minor: bigint): string {
  const dollars = Number(minor / 10_000n) / 100;
  const units: [number, string][] = [
    [1e12, 'T'],
    [1e9, 'B'],
    [1e6, 'M'],
    [1e3, 'K'],
  ];
  for (const [n, suffix] of units) {
    if (dollars < n) continue;
    const v = dollars / n;
    return `$${v >= 100 ? v.toFixed(0) : v >= 10 ? v.toFixed(1) : v.toFixed(2)}${suffix}`;
  }
  return `$${dollars.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}
